import React from 'react';
import {
  Grid,
  CardContent,
  Typography,
  InputAdornment,
  Chip,
  Avatar,
  IconButton,
  Divider,
  CircularProgress,
  Radio,
  RadioGroup,
  FormControlLabel,
  Popper,
  TextField,
  MenuItem,
  Box,
  Switch,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Search as SearchIcon,
  Delete as DeleteIcon,
  LocationOn as LocationIcon,
  ShoppingCart as CartIcon,
  AttachMoney as MoneyIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Store as StoreIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { useSalesStep1 } from './SalesStep1.hooks';
import ProductImagePreview, { ProductImageData } from '../shared/ProductImagePreview';
import RemoteOriginChip from '../shared/RemoteOriginChip';
import {
  MainContainer,
  LeftColumn,
  RightColumn,
  LocationCard,
  ProductSearchCard,
  SearchContainer,
  EmptyStateBox,
  DropdownCard,
  ProductItem,
  CartHeaderBox,
  CartListBox,
  CartEmptyBox,
  CartItemCard,
  SummaryCard,
  ContinueButton,
  WarningBox,
  WarningContent,
  CustomerDisplayBox,
} from './SalesStep1.styles';
import { formatCurrency } from '../../../utils/formatters/money';

const getProductImageData = (product: ProductImageData): ProductImageData => ({
  thumbnail: product.thumbnail || null,
  thumbnailUrl: product.thumbnailUrl || null,
  imagePath: product.imagePath || null,
});

const CatalogSectionHeader: React.FC<{ label: string }> = ({ label }) => (
  <Box
    sx={(theme) => ({
      position: 'sticky',
      top: 0,
      zIndex: 1,
      px: 2,
      py: 0.75,
      bgcolor:
        theme.palette.mode === 'dark' ? theme.palette.background.paper : theme.palette.grey[50],
      borderBottom: `1px solid ${theme.palette.divider}`,
    })}
  >
    <Typography
      variant="caption"
      sx={{
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        color: 'text.secondary',
      }}
    >
      {label}
    </Typography>
  </Box>
);

const SalesStep1: React.FC = () => {
  const {
    // State
    state,
    searchQuery,
    showDropdown,
    isLoadingProducts,
    isLoadingLocations,
    anchorEl,
    availableProducts,
    filteredProducts,
    totalItems,
    canProceedToStep2,
    orderSummary,
    locations,
    sellers,
    currentUser,
    isLoadingSellers,
    catalogScope,
    canBrowseAllBranches,
    canChangeSeller,
    isMixedCart,
    remoteGroupCount,
    isRemoteCartItem,
    // Refs
    searchInputRef,
    // Handlers
    handleSearchChange,
    handleSearchFocus,
    handleSearchBlur,
    handleProductSelect,
    handleLocationChange,
    handleMixedSaleToggle,
    handleSellerChange,
    removeProduct,
    nextStep,
    canChangeSalesBranch,
  } = useSalesStep1();

  return (
    <MainContainer>
      {/* Left Column - Product Selection */}
      <LeftColumn>
        {/* Location Selection */}
        <LocationCard>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocationIcon color="primary" />
              Sucursal de venta
            </Typography>

            {isLoadingLocations ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <RadioGroup value={state.selectedLocation?.id || ''} onChange={handleLocationChange}>
                <Box
                  sx={{
                    maxHeight: 280,
                    overflowY: 'auto',
                    pr: 1,
                  }}
                >
                  <Grid container spacing={2}>
                    {locations.map((location) => (
                      <Grid item xs={12} sm={6} md={4} key={location.id}>
                        <FormControlLabel
                          value={location.id}
                          control={<Radio disabled={!canChangeSalesBranch} />}
                          label={
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                {location.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {location.address}
                              </Typography>
                            </Box>
                          }
                          sx={(theme) => {
                            const selected = state.selectedLocation?.id === location.id;
                            return {
                              width: '100%',
                              m: 0,
                              p: 2,
                              border: 1,
                              borderColor: selected
                                ? theme.palette.primary.main
                                : theme.palette.divider,
                              borderRadius: 2,
                              bgcolor: selected
                                ? theme.palette.mode === 'dark'
                                  ? alpha(theme.palette.primary.main, 0.18)
                                  : alpha(theme.palette.primary.main, 0.08)
                                : 'transparent',
                            };
                          }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </RadioGroup>
            )}

            {/* Origen del catálogo (venta multi-sucursal) */}
            {canBrowseAllBranches && (
              <Box
                sx={(theme) => ({
                  mt: 2,
                  px: 1.5,
                  py: 1,
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  bgcolor:
                    catalogScope === 'all_branches'
                      ? alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.12 : 0.06)
                      : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  transition: 'background-color 0.2s',
                })}
              >
                <StoreIcon sx={{ fontSize: 20, color: 'info.main', flexShrink: 0 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                    ¿Habilitar vender piezas de otras sucursales y almacén?
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', lineHeight: 1.3 }}
                  >
                    {catalogScope === 'all_branches'
                      ? 'Las piezas de otras sucursales o del almacén generarán una solicitud de inventario al finalizar la venta.'
                      : 'Solo se muestran piezas de tu sucursal de venta.'}
                  </Typography>
                </Box>
                <Switch
                  size="small"
                  checked={catalogScope === 'all_branches'}
                  onChange={(_, checked) =>
                    handleMixedSaleToggle(checked ? 'all_branches' : 'selling_branch')
                  }
                  inputProps={{
                    'aria-label': 'Incluir piezas de otras sucursales y almacén',
                  }}
                />
              </Box>
            )}

            {/* Warning about product availability */}
            {!canBrowseAllBranches && (
              <WarningBox>
                <Box sx={{ mt: '2px' }}>
                  <WarningIcon sx={{ color: 'secondary.light' }} />
                </Box>
                <WarningContent>
                  <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
                    Las piezas disponibles para venta varían según la sucursal seleccionada.
                  </Typography>
                </WarningContent>
              </WarningBox>
            )}

            {/* Vendedor de la venta (solo si el usuario puede cambiarlo) */}
            {canChangeSeller && state.selectedLocation && (
              <Box sx={{ mt: 2 }}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Vendedor"
                  value={state.selectedSeller?.id ?? currentUser?.id ?? ''}
                  onChange={(e) => handleSellerChange(e.target.value)}
                  disabled={isLoadingSellers}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                >
                  {currentUser && (
                    <MenuItem value={currentUser.id}>
                      {currentUser.firstName} {currentUser.lastName} (yo)
                    </MenuItem>
                  )}
                  {sellers
                    .filter((seller) => seller.id !== currentUser?.id)
                    .map((seller) => (
                      <MenuItem key={seller.id} value={seller.id}>
                        {seller.firstName} {seller.lastName}
                      </MenuItem>
                    ))}
                </TextField>
              </Box>
            )}
          </CardContent>
        </LocationCard>

        {/* Product Search */}
        <ProductSearchCard>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <SearchIcon color="primary" />
              Buscar piezas
            </Typography>

            <SearchContainer>
              <TextField
                fullWidth
                placeholder="Buscar por nombre, código SKU o categoría..."
                value={searchQuery}
                onChange={handleSearchChange}
                onFocus={handleSearchFocus}
                onBlur={handleSearchBlur}
                inputRef={searchInputRef}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />

              {/* Empty State */}
              {!searchQuery && availableProducts.length === 0 && (
                <EmptyStateBox>
                  <SearchIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                    No hay piezas disponibles
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Selecciona una sucursal para ver las piezas disponibles
                  </Typography>
                </EmptyStateBox>
              )}
            </SearchContainer>

            {/* Popper Dropdown */}
            <Popper
              open={showDropdown && !!anchorEl}
              anchorEl={anchorEl}
              placement="bottom-start"
              style={{ zIndex: 1300 }}
              modifiers={[{ name: 'offset', options: { offset: [0, 4] } }]}
              disablePortal={false}
            >
              <DropdownCard sx={{ width: anchorEl?.offsetWidth || 300 }}>
                <CardContent sx={{ p: 0 }}>
                  {isLoadingProducts ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                      <CircularProgress size={20} />
                    </Box>
                  ) : filteredProducts.length > 0 ? (
                    filteredProducts.map((product, index) => {
                      const isInCart = state.selectedProducts.find(
                        (p) => p.warehouseInventoryProduct.id === product.id,
                      );
                      const sellingId = state.selectedLocation?.id;
                      const showSectionHeaders = catalogScope === 'all_branches';
                      const isRemote =
                        showSectionHeaders &&
                        product.location?.id != null &&
                        product.location.id !== sellingId;
                      const prev = index > 0 ? filteredProducts[index - 1] : null;
                      const prevRemote =
                        prev != null &&
                        showSectionHeaders &&
                        prev.location?.id != null &&
                        prev.location.id !== sellingId;
                      const showLocalHeader =
                        showSectionHeaders && !isRemote && (index === 0 || prevRemote);
                      const showRemoteHeader =
                        showSectionHeaders && isRemote && (index === 0 || !prevRemote);
                      return (
                        <React.Fragment key={product.id}>
                          {showLocalHeader && <CatalogSectionHeader label="En tu sucursal" />}
                          {showRemoteHeader && (
                            <CatalogSectionHeader label="Otras sucursales y almacén" />
                          )}
                          <ProductItem
                            isInCart={!!isInCart}
                            onMouseDown={() => handleProductSelect(product)}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                              }}
                            >
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 600,
                                    mb: 0.5,
                                    color: isInCart ? 'primary.main' : 'text.primary',
                                  }}
                                >
                                  {product.product.name}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}
                                >
                                  Código: {product.productCodeUnique || 'N/A'}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography
                                    variant="caption"
                                    sx={{ fontWeight: 600, color: 'success.main' }}
                                  >
                                    {formatCurrency(
                                      Number(product.salePrice ?? product.product.salePrice ?? 0),
                                    )}
                                  </Typography>
                                  <Chip
                                    label={
                                      typeof product.product.category === 'string'
                                        ? product.product.category
                                        : product.product.category?.name || 'Sin categoría'
                                    }
                                    size="small"
                                    sx={{ height: 20, fontSize: '0.7rem' }}
                                  />
                                  {isRemote && (
                                    <RemoteOriginChip locationName={product.location?.name} />
                                  )}
                                </Box>
                              </Box>
                              <Box sx={{ textAlign: 'right', ml: 1 }}>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontWeight: 500,
                                    color:
                                      product.availabilityStatus === 'AVAILABLE'
                                        ? 'success.main'
                                        : 'error.main',
                                    display: 'block',
                                    mb: 0.5,
                                  }}
                                >
                                  {product.availabilityStatus === 'AVAILABLE'
                                    ? 'Disponible'
                                    : 'No disponible'}
                                </Typography>
                                {isInCart && (
                                  <Chip
                                    label="En carrito"
                                    size="small"
                                    sx={{
                                      height: 20,
                                      fontSize: '0.7rem',
                                      bgcolor: 'primary.main',
                                      color: 'white',
                                    }}
                                  />
                                )}
                              </Box>
                            </Box>
                          </ProductItem>
                        </React.Fragment>
                      );
                    })
                  ) : (
                    <Box sx={{ textAlign: 'center', p: 3 }}>
                      <SearchIcon sx={{ fontSize: 32, color: 'text.disabled', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        No se encontraron piezas
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </DropdownCard>
            </Popper>
          </CardContent>
        </ProductSearchCard>

        {/* Selected Customer Display */}
        {state.selectedCustomer && (
          <CustomerDisplayBox>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Cliente Seleccionado:
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main' }}>
                {state.selectedCustomer.customerName.charAt(0)}
              </Avatar>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {state.selectedCustomer.customerName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {state.selectedCustomer.customerEmail} • {state.selectedCustomer.customerPhone}
                </Typography>
              </Box>
            </Box>
          </CustomerDisplayBox>
        )}
      </LeftColumn>

      {/* Right Column - Selected Products */}
      <RightColumn>
        {/* Header */}
        <CartHeaderBox>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <CartIcon color="primary" />
            Carrito ({state.selectedProducts.length})
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {totalItems === 1 ? '1 pieza seleccionada' : `${totalItems} piezas seleccionadas`}
            {isMixedCart && remoteGroupCount > 0 && (
              <Box component="span" sx={{ color: 'warning.main', fontWeight: 600 }}>
                {' '}
                · {remoteGroupCount} solicitud{remoteGroupCount === 1 ? '' : 'es'} de traslado
              </Box>
            )}
          </Typography>
        </CartHeaderBox>

        {/* Products List */}
        <CartListBox>
          {state.selectedProducts.length === 0 ? (
            <CartEmptyBox>
              <CartIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 600 }}>
                Carrito vacío
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Agrega piezas usando el buscador
              </Typography>
            </CartEmptyBox>
          ) : (
            state.selectedProducts.map((product) => (
              <CartItemCard key={product.warehouseInventoryProduct.id}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <ProductImagePreview
                      product={getProductImageData(
                        product.warehouseInventoryProduct.product as ProductImageData,
                      )}
                      alt={product.warehouseInventoryProduct.product.name}
                      size={72}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          gap: 1,
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, mb: 0.5, lineHeight: 1.2 }}
                          >
                            {product.warehouseInventoryProduct.product.name}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', mb: 0.75 }}
                          >
                            {product.warehouseInventoryProduct.productCodeUnique || 'N/A'}
                          </Typography>
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => removeProduct(product.warehouseInventoryProduct.id)}
                          color="error"
                          sx={{ p: 0.5, mt: -0.25, mr: -0.5, flexShrink: 0 }}
                        >
                          <DeleteIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 0.5,
                          flexWrap: 'wrap',
                        }}
                      >
                        <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 600 }}>
                          {formatCurrency(product.originalPrice)}
                        </Typography>
                        <Chip
                          label={
                            typeof product.warehouseInventoryProduct.product.category === 'string'
                              ? product.warehouseInventoryProduct.product.category
                              : product.warehouseInventoryProduct.product.category?.name ||
                                'Sin categoría'
                          }
                          size="small"
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                        {isRemoteCartItem(product) && (
                          <RemoteOriginChip
                            locationName={product.warehouseInventoryProduct.location?.name}
                          />
                        )}
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </CartItemCard>
            ))
          )}
        </CartListBox>

        {/* Summary and Continue Button */}
        <Box sx={{ mt: 'auto' }}>
          <SummaryCard>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Subtotal:
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formatCurrency(orderSummary.subtotal)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Piezas:
                </Typography>
                <Typography variant="body2">{totalItems} piezas</Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MoneyIcon />
                  Total:
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  {formatCurrency(orderSummary.grandTotal)}
                </Typography>
              </Box>
            </CardContent>
          </SummaryCard>

          <ContinueButton
            fullWidth
            variant="contained"
            size="large"
            disabled={!canProceedToStep2}
            onClick={nextStep}
          >
            Continuar al Paso 2
          </ContinueButton>

          {!canProceedToStep2 && (
            <Box
              sx={(theme) => ({
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5,
                backgroundColor: alpha(
                  theme.palette.success.main,
                  theme.palette.mode === 'dark' ? 0.16 : 0.08,
                ),
                border: `1px solid ${alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.4 : 0.35)}`,
                borderRadius: '8px',
                padding: '10px 14px',
                mt: 2,
              })}
            >
              <InfoIcon sx={{ fontSize: 18, color: 'success.light', mt: '2px', flexShrink: 0 }} />
              <Typography variant="body2" sx={{ color: 'success.light', lineHeight: 1.5 }}>
                Selecciona una sucursal y al menos una pieza para continuar
              </Typography>
            </Box>
          )}
        </Box>
      </RightColumn>
    </MainContainer>
  );
};

export default SalesStep1;
